import { useTranslation } from "react-i18next";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";

type SelectionMode = "single" | "range";

interface IOSCalendarProps {
  selectionMode?: SelectionMode;
  label?: string;
  value?: Date | { from: Date; to: Date };
  onSelect?: (selected: Date | { from: Date; to: Date }) => void;
  onCancel?: () => void;
  minDate?: Date;
  maxDate?: Date;
  getExtraDayClass?: (day: Date) => string | undefined;
}

const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

export const IOSCalendar: React.FC<IOSCalendarProps> = ({
  selectionMode = "single",
  value,
  onSelect,
  onCancel,
  label,
  minDate,
  maxDate,
  getExtraDayClass,
}) => {
  const { t } = useTranslation(["common"]);
  // Initialize selected states based on value prop
  const getInitialSingleValue = (): Date | null => {
    if (value && typeof value === "object" && "from" in value) {
      return null; // Range mode value provided, but we're in single mode
    }
    return (value as Date) || null;
  };

  const getInitialRangeValues = (): {
    start: Date | null;
    end: Date | null;
  } => {
    if (value && typeof value === "object" && "from" in value) {
      return { start: value.from, end: value.to };
    }
    return { start: null, end: null };
  };

  const [currentMonth, setCurrentMonth] = useState(() => {
    // Set initial month based on value
    if (value) {
      if (typeof value === "object" && "from" in value) {
        return new Date(value.from);
      } else {
        return new Date(value as Date);
      }
    }
    return new Date();
  });
  const [selectedSingle, setSelectedSingle] = useState<Date | null>(getInitialSingleValue);
  const [rangeStart, setRangeStart] = useState<Date | null>(getInitialRangeValues().start);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(getInitialRangeValues().end);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<"date" | "month" | "year">("date");

  const yearContainerRef = useRef<HTMLDivElement | null>(null);
  const currentYearRef = useRef<HTMLDivElement | null>(null);

  const daysOfWeek = useMemo(() => {
    const start = startOfWeek(new Date(), { weekStartsOn: 0 }); // Sunday
    return Array.from({ length: 7 }).map((_, i) => format(addDays(start, i), "EEEEE"));
  }, []);

  const monthMatrix = useMemo(() => {
    const startMonth = startOfMonth(currentMonth);
    const endMonth = endOfMonth(currentMonth);
    const startDate = startOfWeek(startMonth, { weekStartsOn: 0 });
    const endDate = endOfWeek(endMonth, { weekStartsOn: 0 });

    const rows: Date[][] = [];
    let cursor = startDate;
    while (cursor <= endDate) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(cursor);
        cursor = addDays(cursor, 1);
      }
      rows.push(week);
    }
    return rows;
  }, [currentMonth]);

  const inSelectedRange = (day: Date) => {
    if (selectionMode === "single") return false;
    if (rangeStart && rangeEnd) {
      return isWithinInterval(day, { start: rangeStart, end: rangeEnd });
    }
    if (rangeStart && hoverDate) {
      const [from, to] = rangeStart <= hoverDate ? [rangeStart, hoverDate] : [hoverDate, rangeStart];
      return isWithinInterval(day, { start: from, end: to });
    }
    return false;
  };

  const handleDayClick = (day: Date) => {
    if (selectionMode === "single") {
      setSelectedSingle(day);
    } else {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(day);
        setRangeEnd(null);
      } else if (rangeStart && !rangeEnd) {
        if (day < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(day);
        } else {
          setRangeEnd(day);
        }
      }
    }
  };

  const handleMonthClick = (monthIndex: number) => {
    setSelectedSingle(null);
    const newDate = new Date(currentMonth.getFullYear(), monthIndex, 1);
    setCurrentMonth(newDate);
    setViewMode("date");
  };

  const handleYearClick = (year: number) => {
    setSelectedSingle(null);
    const newDate = new Date(year, currentMonth.getMonth(), 1);
    setCurrentMonth(newDate);
    setViewMode("month");
  };

  // Helper to check if a date is disabled
  const isDateDisabled = (date: Date) => {
    if (minDate && date < startOfDay(minDate)) return true;
    if (maxDate && date > endOfDay(maxDate)) return true;
    return false;
  };

  const header = (
    <div className="flex items-center justify-between mb-2 border-b border-surface-elevated p-2">
      <p className="text-xs font-medium truncate max-w-44 text-ellipsis overflow-clip capitalize">{label}</p>
      <div className="flex items-center justify-between gap-1 min-w-40">
        <div
          aria-label={t("common:buttons.previous")}
          className="p-1 rounded-full text-lg cursor-pointer"
          onClick={() => {
            if (viewMode === "date") setCurrentMonth((m) => subMonths(m, 1));
            else if (viewMode === "month") setCurrentMonth((m) => subMonths(m, 12));
            else if (viewMode === "year") setCurrentMonth((m) => subMonths(m, 48)); // jump back 4 years block
          }}
        >
          <ChevronLeft size={16} />
        </div>

        <div className="flex flex-col items-center select-none">
          <div className="flex items-baseline gap-1">
            <button
              aria-label={t("common:a11y.pick_month")}
              onClick={() => setViewMode("month")}
              className="text-xs  text-primary font-medium hover:underline"
              type="button"
            >
              {format(currentMonth, "MMMM")}
            </button>
            <button
              aria-label={t("common:a11y.pick_year")}
              onClick={() => setViewMode("year")}
              className="text-xs  text-primary font-medium hover:underline"
              type="button"
            >
              {format(currentMonth, "yyyy")}
            </button>
          </div>
        </div>

        <div
          aria-label={t("common:buttons.next")}
          onClick={() => {
            if (viewMode === "date") setCurrentMonth((m) => addMonths(m, 1));
            else if (viewMode === "month") setCurrentMonth((m) => addMonths(m, 12));
            else if (viewMode === "year") setCurrentMonth((m) => addMonths(m, 48)); // jump forward block
          }}
          className="p-1 rounded-full text-lg cursor-pointer"
        >
          <ChevronRight size={16} />
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (viewMode === "year" && currentYearRef.current) {
      currentYearRef.current.scrollIntoView({
        block: "center",
        behavior: "instant",
      });
    }
  }, [viewMode, currentMonth]);

  return (
    <div className="w-full bg-white rounded-sxs border border-surface-elevated font-sans select-none">
      {header}

      {(viewMode === "month" || viewMode === "year") && (
        <div className="flex justify-end px-2">
          <button onClick={() => setViewMode("date")} className="text-xs text-gray-500 hover:underline" type="button">
            {t("common:buttons.back")}
          </button>
        </div>
      )}

      {viewMode === "month" && (
        <div className="grid grid-cols-3 gap-1 p-2">
          {Array.from({ length: 12 }).map((_, idx) => {
            const monthDate = new Date(currentMonth.getFullYear(), idx, 1);
            const isCurrent = idx === currentMonth.getMonth();
            return (
              <div
                key={idx}
                onClick={() => handleMonthClick(idx)}
                className={`cursor-pointer py-1 px-1 flex justify-center items-center rounded-sxs text-xs ${isCurrent ? "bg-info-muted text-white font-semibold" : "hover:bg-gray-100 text-gray-800"
                  }`}
              >
                {format(monthDate, "MMM")}
              </div>
            );
          })}
        </div>
      )}

      {viewMode === "year" && (
        <div
          ref={yearContainerRef}
          className="max-h-[200px] overflow-auto p-2 grid grid-cols-4 gap-1"
        >
          {range(1900, 2100).map((yr) => {
            const isCurrent = yr === currentMonth.getFullYear();

            return (
              <div
                ref={isCurrent ? currentYearRef : null}
                key={yr}
                onClick={() => handleYearClick(yr)}
                className={`cursor-pointer py-1 px-1 flex justify-center items-center rounded-sxs text-xs ${isCurrent
                  ? "bg-info-muted text-white font-semibold"
                  : "hover:bg-gray-100 text-gray-800"
                  }`}
              >
                {yr}
              </div>
            );
          })}
        </div>
      )}


      {viewMode === "date" && (
        <>
          <div className="grid grid-cols-7 text-center text-xs font-medium text-gray-500 mb-1 px-1">
            {daysOfWeek.map((d, i) => (
              <div key={`${d}-${i}`} className="text-[10px]">
                {d}
              </div>
            ))}
          </div>
          <div className="space-y-1 px-1">
            {monthMatrix.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day) => {
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const selected =
                    selectionMode === "single"
                      ? selectedSingle && isSameDay(day, selectedSingle)
                      : (rangeStart && isSameDay(day, rangeStart)) || (rangeEnd && isSameDay(day, rangeEnd));
                  const inRange = inSelectedRange(day);
                  const disabled = isDateDisabled(day);
                  const extraClass =
                    isCurrentMonth && !disabled && !selected
                      ? (getExtraDayClass?.(day) ?? "")
                      : "";
                  return (
                    <div
                      key={day.toISOString()}
                      onClick={() => !disabled && handleDayClick(day)}
                      onMouseEnter={() => !disabled && setHoverDate(day)}
                      onMouseLeave={() => !disabled && setHoverDate(null)}
                      className={`
                        relative flex items-center justify-center h-8
                        ${!isCurrentMonth
                          ? "text-gray-300"
                          : disabled
                            ? "text-gray-300 cursor-not-allowed bg-gray-100"
                            : "text-gray-900 cursor-pointer"
                        }
                        ${inRange && !selected && !disabled ? "bg-info-muted rounded-full" : ""}
                      `}
                    >
                      <div
                        className={`
                          z-10 flex items-center justify-center w-7 h-7
                          text-[10px]
                          ${selected && !disabled
                            ? "bg-info-muted text-primary rounded-sxs font-semibold "
                            : extraClass
                            ? `${extraClass} rounded-full`
                            : "rounded-sxs"
                          }
                          transition
                        `}
                      >
                        {format(day, "d")}
                      </div>
                      {selectionMode === "range" && selected && !disabled && (
                        <div className="absolute inset-0 rounded-full ring-2 ring-info" aria-hidden="true" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 p-3 mt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            setSelectedSingle(null);
            setRangeStart(null);
            setRangeEnd(null);
            setHoverDate(null);
            onCancel?.();
          }}
          className="flex-1 text-xs h-fit! py-2 rounded-sxs"
        >
          {t("common:buttons.cancel")}
        </Button>
        <Button
          size="sm"
          disabled={
            (!selectedSingle && !rangeStart && !rangeEnd) ||
            (selectedSingle !== null && isDateDisabled(selectedSingle)) ||
            (rangeStart !== null && isDateDisabled(rangeStart)) ||
            (rangeEnd !== null && isDateDisabled(rangeEnd))
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (selectionMode === "single" && selectedSingle) {
              onSelect?.(selectedSingle);
            } else if (selectionMode === "range" && rangeStart && rangeEnd) {
              onSelect?.({ from: rangeStart, to: rangeEnd });
            }
          }}
          className="flex-1 h-fit! py-2 text-xs text-white rounded-xs"
        >
          {t("common:buttons.save_plain")}
        </Button>
      </div>
    </div>
  );
};
